import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { supabase } from '../lib/supabase';

const schema = z.object({
  password: z.string().min(8, 'Password must be at least 8 characters'),
  confirm: z.string(),
})
  .refine((d) => d.confirm.length > 0, {
    path: ['confirm'],
    message: 'Please confirm your password',
  })
  .refine((d) => d.password === d.confirm, {
    path: ['confirm'],
    message: 'Passwords do not match',
  });

type FormData = z.infer<typeof schema>;

export default function ResetPassword() {
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState(false);
  // True once Supabase has parsed the recovery hash and produced a
  // password-recovery session. Until then we don't render the form, to
  // avoid users submitting before the session is in place.
  const [readyForReset, setReadyForReset] = useState(false);

  const { register, handleSubmit, formState: { errors } } = useForm<FormData>({
    resolver: zodResolver(schema),
  });

  useEffect(() => {
    // Supabase JS auto-parses the URL hash on init. The PASSWORD_RECOVERY
    // event fires when the recovery token is detected and a temporary
    // session is established. We also accept an existing session that
    // was set just before mount.
    //
    // `ready` is a closure-scoped flag (NOT React state) so the timeout
    // callback below reads the latest value, not the stale initial-render
    // value of readyForReset. Setting state from the same call keeps the
    // UI in sync.
    let ready = false;
    const markReady = () => {
      ready = true;
      setReadyForReset(true);
    };

    const { data: sub } = supabase.auth.onAuthStateChange((event) => {
      if (event === 'PASSWORD_RECOVERY' || event === 'SIGNED_IN') {
        markReady();
      }
    });

    supabase.auth.getSession().then(({ data }) => {
      if (data.session) markReady();
    });

    // If the URL has no recovery hash and no session lands within ~5s,
    // surface a helpful error so the user knows the link is bad/expired.
    // 5s is enough headroom for Supabase to parse the hash and create
    // the session on slow networks; faster than 2s caused false positives.
    const timeout = setTimeout(() => {
      if (!ready) {
        setError('Reset link is invalid or expired. Request a new one from the Forgot password page.');
      }
    }, 5000);

    return () => {
      sub.subscription.unsubscribe();
      clearTimeout(timeout);
    };
  }, []);

  const onSubmit = async (data: FormData) => {
    if (isLoading) return;
    setIsLoading(true);
    setError(null);

    const { error: updateError } = await supabase.auth.updateUser({
      password: data.password,
    });

    if (updateError) {
      console.error('[ResetPassword]', updateError);
      setError(updateError.message);
      setIsLoading(false);
      return;
    }

    setDone(true);
    setIsLoading(false);

    // Sign out the temporary recovery session and send the user back to
    // /login so they sign in with the new password.
    await supabase.auth.signOut();
    setTimeout(() => navigate('/login', { replace: true }), 2500);
  };

  return (
    <div className="min-h-screen flex items-start justify-start pt-[8vh] pb-6 px-4 sm:px-6 lg:pl-[8%] auth-bg">
      <div className="fixed inset-0 bg-navy/70" aria-hidden="true" />
      <div className="relative max-w-md w-full space-y-4">
        <div className="w-full">
          <div className="mb-4 flex justify-center">
            <img
              src="/images/wingmentor-logo.png"
              alt="WingMentor Logo"
              className="h-20 w-auto opacity-70"
              onError={(e) => { e.currentTarget.style.display = 'none'; }}
            />
          </div>
          <h2 className="mb-1 text-center text-xl font-heading font-semibold text-white">
            Choose a new password
          </h2>
        </div>

        <div className="space-y-4 rounded-xl border-2 border-orca-elevate/15 bg-white/50 px-7 py-5 shadow-2xl backdrop-blur-sm backdrop-saturate-125 ring-1 ring-white/35">
          {done ? (
            <div className="bg-green-50 border-l-4 border-green-500 text-navy px-4 py-3 rounded">
              <p className="font-medium text-sm">Password updated</p>
              <p className="text-sm mt-1">Redirecting to sign in…</p>
            </div>
          ) : !readyForReset && !error ? (
            <p className="text-sm text-navy text-center py-2">Verifying reset link…</p>
          ) : (
            <form className="space-y-4" onSubmit={handleSubmit(onSubmit)}>
              {error && (
                <div className="bg-signal/10 border-l-4 border-signal text-navy px-4 py-2.5 rounded">
                  <p className="font-medium text-sm">Error</p>
                  <p className="text-sm">{error}</p>
                </div>
              )}

              <div>
                <label htmlFor="password" className="block text-sm font-medium text-navy mb-1">
                  New password
                </label>
                <input
                  id="password"
                  type="password"
                  autoComplete="new-password"
                  autoFocus
                  {...register('password')}
                  className="appearance-none relative block w-full px-3 py-2 bg-white border border-steel/30 rounded-md placeholder-steel/50 text-navy focus:outline-none focus:ring-2 focus:ring-orca-elevate focus:border-orca-elevate sm:text-sm"
                  placeholder="••••••••"
                />
                {errors.password && (
                  <p className="mt-1 text-sm text-red-600">{errors.password.message}</p>
                )}
              </div>

              <div>
                <label htmlFor="confirm" className="block text-sm font-medium text-navy mb-1">
                  Confirm new password
                </label>
                <input
                  id="confirm"
                  type="password"
                  autoComplete="new-password"
                  {...register('confirm')}
                  className="appearance-none relative block w-full px-3 py-2 bg-white border border-steel/30 rounded-md placeholder-steel/50 text-navy focus:outline-none focus:ring-2 focus:ring-orca-elevate focus:border-orca-elevate sm:text-sm"
                  placeholder="••••••••"
                />
                {errors.confirm && (
                  <p className="mt-1 text-sm text-red-600">{errors.confirm.message}</p>
                )}
              </div>

              <button
                type="submit"
                disabled={isLoading || !readyForReset}
                className="btn-primary w-full disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isLoading ? 'Updating…' : 'Update password'}
              </button>

              <div className="text-sm text-center">
                <Link
                  to="/login"
                  className="font-medium text-orca-elevate hover:text-orca-elevate/80"
                >
                  Back to sign in
                </Link>
              </div>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
